import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Box,
  TextField
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AdminLayout from "./Admin_Layout/AdminLayout";

const EnquiryTable = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    name: "",
    school: "",
    city: "",
    email: "",
    contactNo: "",
    message: "",
    date: null, // Filter for enquiry date
    preferredDateTime: null, // Filter for preferred date and time
  });

  // Fetch the enquiries and apply sorting
  useEffect(() => {
    const fetchEnquiries = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/enquiry`);

        // Sort from latest to oldest based on date
        const sortedData = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
        setEnquiries(sortedData);
        setFilteredEnquiries(sortedData);
      } catch (err) {
        setError("Failed to fetch enquiries");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchEnquiries();
  }, []);

  // Filter change handler
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters, [name]: value };
      applyFilters(updatedFilters);
      return updatedFilters;
    });
  };

  // Date filter change handler
  const handleDateFilterChange = (name, value) => {
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters, [name]: value };
      applyFilters(updatedFilters);
      return updatedFilters;
    });
  };

  // Apply filters to enquiries
  const applyFilters = (filters) => {
    const filteredData = enquiries.filter((enquiry) =>
      Object.keys(filters).every((key) => {
        if (!filters[key]) return true; // Skip if filter is empty

        // Handle date filters
        if (key === "date" || key === "preferredDateTime") {
          const filterDate = dayjs(filters[key]).startOf("day");
          const enquiryDate = dayjs(enquiry[key]).startOf("day");
          return filterDate.isSame(enquiryDate);
        }

        // Handle text filters
        return enquiry[key]?.toString().toLowerCase().includes(filters[key].toLowerCase());
      })
    );
    setFilteredEnquiries(filteredData);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  return (
    <AdminLayout>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TableContainer component={Paper} sx={{ maxWidth: "99%", margin: "auto", mt: 8 }}>
          <Typography variant="h5" align="center" gutterBottom sx={{ mt: 2, fontWeight: "bold" }}>
            Enquiry List
          </Typography>

          {/* Filter Inputs */}
          <Box display="flex" justifyContent="space-between" flexWrap="wrap" p={2}>
            {Object.keys(filters).map((key) => {
              if (key === "date" || key === "preferredDateTime") {
                return (
                  <DatePicker
                    key={key}
                    label={`Filter by ${key}`}
                    value={filters[key]}
                    sx={{ mb: 2, mr: 1, width: "30%" }}

                    onChange={(newValue) => handleDateFilterChange(key, newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={{ mb: 2, mr: 1, width: "30%" }}
                      />
                    )}
                  />
                );
              }
              return (
                <TextField
                  key={key}
                  name={key}
                  label={`Filter by ${key}`}
                  variant="outlined"
                  size="small"
                  value={filters[key]}
                  onChange={handleFilterChange}
                  sx={{ mb: 2, mr: 1, width: "30%" }}
                />
              );
            })}
          </Box>

          <Table>
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>School</strong></TableCell>
                <TableCell><strong>City</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Contact No</strong></TableCell>
                <TableCell><strong>Message</strong></TableCell>
                <TableCell><strong>Preferred Date & Time</strong></TableCell>
                <TableCell><strong>Enquiry Date</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEnquiries.map((enquiry, index) => (
                <TableRow key={index}>
                  <TableCell>{enquiry.name}</TableCell>
                  <TableCell>{enquiry.school}</TableCell>
                  <TableCell>{enquiry.city}</TableCell>
                  <TableCell>{enquiry.email}</TableCell>
                  <TableCell>{enquiry.contactNo}</TableCell>
                  <TableCell>{enquiry.message || "N/A"}</TableCell>
                  <TableCell>{new Date(enquiry.preferredDateTime).toLocaleString()}</TableCell>
                  <TableCell>{new Date(enquiry.date).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </LocalizationProvider>
    </AdminLayout>
  );
};

export default EnquiryTable;